import React, { useRef, useState, useEffect } from "react";
import { useDrop } from "react-dnd";
import { Document, Page, pdfjs } from "react-pdf";
import DraggableResizableField from "./DraggableResizableField";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const PdfDropZone = ({
  pdfUrl,
  editFields,
  signFields,
  addField,
  editorMode,
  handleFieldDelete,
  handleFieldResize,
  handleUpdateFields,
  setPdfDimensions,
  pdfDimensions,
}) => {
  const pdfContainerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [pdfScale, setPdfScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [droppedField, setDroppedField] = useState(null);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "FIELD",
      drop: (item, monitor) => {
        const offset = monitor.getSourceClientOffset();
        if (offset && pdfContainerRef.current) {
          const rect = pdfContainerRef.current.getBoundingClientRect();
          const x = offset.x - rect.left;
          const y = offset.y - rect.top;

          const page = Math.floor((y * numPages) / rect.height) + 1;
          let width = 200;
          let height = 100;
          if (item.type.toUpperCase() === "TEXT") {
            width = 200;
            height = 40;
          }
          setDroppedField({
            type: item.type,
            name: item.name || item.type,
            width: width,
            height: height,
            top: y - (rect.height / numPages) * (page - 1),
            left: x,
            page: page,
            target: editorMode, // Ensure target mode is correctly set
          });
        }
      },
      collect: (monitor) => ({ isOver: !!monitor.isOver() }),
    }),
    [editorMode, numPages]
  );

  // Apply the dropped field after rendering
  useEffect(() => {
    if (droppedField) {
      addField(
        droppedField.type,
        droppedField.name,
        droppedField.top,
        droppedField.left,
        droppedField.width,
        droppedField.height,
        droppedField.page,
        droppedField.target // Make sure this is correctly passed
      );
      setDroppedField(null);
    }
  }, [droppedField, addField]);

  const onDocumentLoad = async ({ numPages }) => {
    setNumPages(numPages);

    if (!pdfUrl) return;

    try {
      // Load the PDF document
      const loadingTask = pdfjs.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;

      if (!pdf) {
        console.error("PDF document is null");
        return;
      }

      const firstPage = await pdf.getPage(1); // Get the first page

      if (!firstPage) {
        console.error("First page is null");
        return;
      }

      // Use getViewport to get the correct height & width
      const viewport = firstPage.getViewport({ scale: 2 });
      const { width, height } = viewport;

      // Adjust dimensions
      setPdfDimensions({ width: width, height: height });

      setContainerWidth(width); // Ensure a reasonable width
    } catch (error) {
      console.error("Error getting PDF page dimensions:", error);
    }
  };
  return (
    <div
      ref={drop}
      style={{
        flex: 1,
        overflow: "auto",
        position: "relative",
        width: `${containerWidth}px`,
        margin: "0 auto",
        backgroundColor: "#f0f0f0",
      }}
    >
      <div ref={pdfContainerRef} style={{ position: "relative" }}>
        <Document file={pdfUrl} onLoadSuccess={onDocumentLoad}>
          {[...Array(numPages)].map((_, index) => {
            const pageNumber = index + 1;
            return (
              <div
                key={pageNumber}
                style={{
                  position: "relative",
                  width: `${containerWidth}px`,
                  height: `1100 px`,
                  marginBottom: "10px",
                }}
              >
                <Page
                  pageNumber={pageNumber}
                  width={containerWidth}
                  renderAnnotationLayer={true}
                  renderTextLayer={true}
                />

                {/* Render sign fields only if in sign mode */}
                {editorMode === "sign" &&
                  signFields.map((field) => {
                    if (field.page === pageNumber) {
                      return (
                        <DraggableResizableField
                          key={`sign-${field.id}`}
                          field={field}
                          onDrag={(x, y) =>
                            handleUpdateFields(
                              field.id,
                              field.type,
                              field.name,
                              y,
                              x,
                              field.width,
                              field.height,
                              pageNumber,
                              "sign"
                            )
                          }
                          onResize={(width, height) =>
                            handleFieldResize(field.id, width, height, "sign")
                          }
                          pdfScale={pdfScale}
                          onDelete={() => handleFieldDelete(field.id, "sign")}
                        />
                      );
                    }
                  })}

                {/* Render edit fields only if in edit mode */}
                {editorMode === "edit" &&
                  editFields.map((field) => {
                    if (field.page === pageNumber) {
                      return (
                        <DraggableResizableField
                          key={`edit-${field.id}`}
                          field={field}
                          onDrag={(x, y) =>
                            handleUpdateFields(
                              field.id,
                              field.type,
                              field.name,
                              y,
                              x,
                              field.width,
                              field.height,
                              pageNumber,
                              "edit"
                            )
                          }
                          onResize={(width, height) =>
                            handleFieldResize(field.id, width, height, "edit")
                          }
                          pdfScale={pdfScale}
                          onDelete={() => handleFieldDelete(field.id, "edit")}
                          onTextEdit={(text) => {
                            handleUpdateFields(
                              field.id,
                              field.type,
                              text,
                              field.top,
                              field.left,
                              field.width,
                              field.height,
                              pageNumber,
                              "edit"
                            );
                          }}
                          editorMode="edit"
                        />
                      );
                    }
                  })}
              </div>
            );
          })}
        </Document>
      </div>
    </div>
  );
};

export default PdfDropZone;
